import React, { useEffect, useState } from 'react';
import { Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { isUlid } from './paths';
import { getBySlug } from './api';

/*
 * Every route under /courses, in one place so App.js and the route-ranking
 * test use the same table.
 *
 * AI courses live at /courses/<slug>, beside the LMS's own /courses/<slug>.
 * A course id (ULID) is always an AI course; a slug is one when the course
 * builder knows it (GET /ai-courses/by-slug), and the LMS's otherwise — see
 * AiOrLms. The static segments (new, s, …/build, …/edit) outrank both.
 *
 * The old /courses/ai/… addresses redirect to the same path without /ai, so
 * links shared before the move keep working.
 *
 * `Private` wraps members-only pages (App.js's PrivateRoute). The hub and the
 * viewer are public: the hub asks visitors to sign in per tab, and the viewer's
 * access is decided server-side (CONTRACTS.md, "Web routes").
 */

/* /<base>/ai/<rest> → /<base>/<rest>, query and hash kept. */
export function DropAi({ base }) {
  const loc = useLocation();
  return <Navigate to={`${base}${loc.pathname.slice(`${base}/ai`.length)}${loc.search}${loc.hash}`} replace />;
}

/* slug → 'ai' | 'lms', for the lifetime of the page. */
const kinds = new Map();

/*
 * /courses/:courseId(/lessons/:lessonId): the AI viewer or the LMS page.
 * Only a 404 from the slug lookup means "not an AI course"; any other failure
 * shows the AI viewer, which reports the error itself.
 */
export function AiOrLms({ ai, lms, resolveSlug = getBySlug }) {
  const { courseId } = useParams();
  const key = String(courseId || '').toLowerCase();
  const known = isUlid(courseId) ? 'ai' : kinds.get(key);
  const [kind, setKind] = useState(null);
  useEffect(() => {
    if (known) return undefined;
    let live = true;
    setKind(null);
    resolveSlug(key).then(
      () => { kinds.set(key, 'ai'); if (live) setKind('ai'); },
      (e) => {
        const notFound = (e?.response?.status || e?.status) === 404;
        if (notFound) kinds.set(key, 'lms');
        if (live) setKind(notFound ? 'lms' : 'ai');
      },
    );
    return () => { live = false; };
  }, [key, known, resolveSlug]);
  const k = known || kind;
  if (!k) return null;
  return k === 'ai' ? ai : lms;
}

export function courseRoutes({ Private, pages, resolveSlug }) {
  const { Courses, CourseDetail, CoursePlayer, Wizard, Viewer, Build, Editor } = pages;
  return [
    <Route key="cb-ai" path="/courses/ai" element={<Navigate to="/courses?tab=mine" replace />} />,
    <Route key="cb-ai-old" path="/courses/ai/*" element={<DropAi base="/courses" />} />,
    <Route key="cb-new" path="/courses/new" element={<Private><Wizard /></Private>} />,
    <Route key="cb-link" path="/courses/s/:token" element={<Viewer mode="link" />} />,
    <Route key="cb-link-lesson" path="/courses/s/:token/lessons/:lessonId" element={<Viewer mode="link" />} />,
    <Route key="cb-build" path="/courses/:courseId/build" element={<Private><Build /></Private>} />,
    <Route key="cb-edit" path="/courses/:courseId/edit" element={<Private><Editor /></Private>} />,
    <Route key="courses" path="/courses" element={<Courses />} />,
    <Route key="course" path="/courses/:courseId"
      element={<AiOrLms resolveSlug={resolveSlug} ai={<Viewer mode="course" />} lms={<Private><CourseDetail /></Private>} />} />,
    <Route key="course-lesson" path="/courses/:courseId/lessons/:lessonId"
      element={<AiOrLms resolveSlug={resolveSlug} ai={<Viewer mode="course" />} lms={<Private><CoursePlayer /></Private>} />} />,
  ];
}
